"use client";

import React from "react";

import type { T_ChartData } from "@lattice/core-lib/types/chart";

import { DualAxisBarLineChart } from "./DualAxisBarLineChart";
import { ExpectedActualChart } from "./ExpectedActualChart";
import { LineChart } from "./LineChart";
import { MultiLineChart } from "./MultiLineChart";
import "./charts-kit.css";

export type T_ChartRendererProps = {
  chart: T_ChartData;
  height?: number;
  syntheticFallback?: boolean;
  /** Single-series line only: trend-derived stroke + %-change readout. */
  trendColoring?: boolean;
  /** Single-series line only: series display name for the header. */
  valueName?: string;
};

function isMultiSeriesChart(chart: T_ChartData): boolean {
  if (chart.series && chart.series.length > 1) return true;
  const firstPoint = chart.data[0];
  if (!firstPoint || typeof firstPoint !== "object") return false;
  return !("value" in firstPoint);
}

/**
 * Renderer dispatch for validated z_ChartData — web mirror of the mobile
 * dispatch gate. Specialized visual grammar (`presentation`) is additive
 * metadata over ordinary series: `expected_actual` and `dual_axis_bar_line`
 * get their bespoke renderers, everything else (including unknown future
 * presentation kinds) falls back to the generic line renderers.
 */
export function ChartRenderer({
  chart,
  height,
  syntheticFallback,
  trendColoring,
  valueName,
}: T_ChartRendererProps) {
  const presentation = chart.presentation;

  if (presentation?.kind === "expected_actual") {
    // Hand-SVG presentations size themselves (width-driven aspect ratio
    // matching mobile); a fixed pixel height would stretch the geometry.
    return <ExpectedActualChart chart={chart} presentation={presentation} />;
  }

  if (presentation?.kind === "dual_axis_bar_line") {
    return <DualAxisBarLineChart chart={chart} presentation={presentation} />;
  }

  if (isMultiSeriesChart(chart)) {
    return (
      <MultiLineChart
        chart={chart}
        height={height}
        syntheticFallback={syntheticFallback}
      />
    );
  }

  return (
    <LineChart
      chart={chart}
      height={height}
      syntheticFallback={syntheticFallback}
      trendColoring={trendColoring}
      valueName={valueName}
    />
  );
}
